import React from "react";
import { Button, Typography, Box, Paper } from "@material-ui/core";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("🚨 Erro capturado pelo ErrorBoundary:", error, errorInfo);
    
    // Log detalhado para debug
    console.error("Stack trace:", error.stack);
    console.error("Component stack:", errorInfo.componentStack);
    
    this.setState({
      error,
      errorInfo
    });

    // Tentar limpar localStorage corrompido se o erro estiver relacionado
    if (error?.message?.includes("JSON") || error?.message?.includes("localStorage")) {
      try {
        console.warn("⚠️ Tentando limpar localStorage corrompido...");
        this.clearCorruptedLocalStorage();
      } catch (e) {
        console.error("Erro ao limpar localStorage:", e);
      }
    }
  }

  clearCorruptedLocalStorage = () => {
    try {
      // Lista de chaves conhecidas que devem ser JSON
      const jsonKeys = [
        "cshow",
        "sendSignMessage",
        "profileImage",
        "frontendVersion",
        "push-notification-asked"
      ];

      // Chaves que são strings simples (não JSON): cores, nome, tema, logos (filename)
      const stringKeys = [
        "token",
        "public-token",
        "preferredTheme",
        "primaryColorLight",
        "primaryColorDark",
        "appName",
        "appLogoLight",
        "appLogoDark",
        "appLogoFavicon"
      ];

      let cleanedCount = 0;
      const corruptedKeys = [];

      // Verificar TODAS as chaves do localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;

        try {
          const item = localStorage.getItem(key);
          if (!item) continue;

          // Se for uma chave que deve ser JSON, tentar parsear
          if (jsonKeys.includes(key)) {
            try {
              JSON.parse(item);
            } catch (e) {
              corruptedKeys.push(key);
              cleanedCount++;
            }
          }
          // Para outras chaves, verificar se não são strings muito grandes ou inválidas
          else if (!stringKeys.includes(key)) {
            // Tentar detectar se é JSON corrompido
            if ((item.startsWith('{') || item.startsWith('[')) && item.length > 0) {
              try {
                JSON.parse(item);
              } catch (e) {
                corruptedKeys.push(key);
                cleanedCount++;
              }
            }
          }
        } catch (e) {
          corruptedKeys.push(key);
          cleanedCount++;
        }
      }

      // Remover chaves corrompidas
      corruptedKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`✅ Chave removida: ${key}`);
        } catch (e) {
          console.error(`Erro ao remover chave ${key}:`, e);
        }
      });

      // Verificar se o token existe e parece válido
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const parts = token.split('.');
          if (parts.length !== 3) {
            console.warn("⚠️ Token parece inválido. Removendo...");
            localStorage.removeItem("token");
            cleanedCount++;
          }
        } catch (e) {
          console.warn("⚠️ Erro ao validar token. Removendo...");
          localStorage.removeItem("token");
          cleanedCount++;
        }
      }

      if (cleanedCount > 0) {
        console.log(`✅ Limpeza concluída: ${cleanedCount} chave(s) removida(s)`);
      }

      return cleanedCount;
    } catch (e) {
      console.error("Erro ao limpar localStorage:", e);
      // Se houver erro crítico, tentar limpar tudo exceto o token
      try {
        const token = localStorage.getItem("token");
        localStorage.clear();
        if (token) {
          try {
            localStorage.setItem("token", token);
          } catch (e2) {
            console.error("Erro ao restaurar token:", e2);
          }
        }
        console.log("✅ localStorage limpo completamente (exceto token)");
      } catch (e2) {
        console.error("Erro crítico ao limpar localStorage:", e2);
      }
      return 0;
    }
  };

  handleReset = () => {
    // Limpar localStorage problemático
    this.clearCorruptedLocalStorage();
    
    // Recarregar a página
    window.location.reload();
  };

  handleClearAll = () => {
    try {
      // Salvar apenas o token se existir (para não perder sessão)
      const token = localStorage.getItem("token");
      localStorage.clear();
      if (token) {
        try {
          localStorage.setItem("token", token);
        } catch (e) {
          console.error("Erro ao restaurar token:", e);
        }
      }
      window.location.reload();
    } catch (e) {
      console.error("Erro ao limpar localStorage:", e);
      // Forçar reload mesmo assim
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="background.default"
          p={3}
        >
          <Paper
            elevation={3}
            style={{
              padding: "2rem",
              maxWidth: "600px",
              textAlign: "center"
            }}
          >
            <Typography variant="h5" gutterBottom color="error">
              ⚠️ Ops! Algo deu errado
            </Typography>
            <Typography variant="body1" style={{ marginTop: "1rem", marginBottom: "2rem" }}>
              Ocorreu um erro ao carregar a aplicação. Isso pode ser causado por dados corrompidos no navegador.
            </Typography>
            
            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  textAlign: "left",
                  maxHeight: "200px",
                  overflow: "auto"
                }}
              >
                <Typography variant="caption" style={{ fontFamily: "monospace" }}>
                  {this.state.error.toString()}
                </Typography>
              </Box>
            )}

            <Box display="flex" gap={2} justifyContent="center" marginTop="2rem">
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleReset}
              >
                Tentar Novamente
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={this.handleClearAll}
              >
                Limpar Dados e Recarregar
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
